import { z } from 'zod'

import { AccessModifierToStudentSchema } from '~/@entities/Content'
import { CurriculumTypeSchema } from '~/@entities/Curriculum'
import { LessonClassIdSchema } from '~/@entities/LessonClass'
import {
  LayoutColorSchema,
  PartitionTypeSchema,
  PdfDateTypeSchema,
} from '~/@entities/PrintDocument'
import { ProblemLevelSchema } from '~/@entities/Problem'
import { RegionSchema } from '~/@entities/Region'
import {
  AllGradeSubjectSchema,
  RevisionSchema,
  SchoolTypeSchema,
  SemesterSchema,
} from '~/@entities/SchoolSystem'
import { SchoolTestTypeSchema } from '~/@entities/SchoolTest'
import { StudentIdSchema, StudentStatusSchema } from '~/@entities/Student'
import { TeacherIdSchema, TeacherSchema } from '~/@entities/Teacher'
import { WorkbookSecuringTypeSchema, WorkbookTypeSchema } from '~/@entities/Workbook'

/***************** 학습지 종류 *****************/
export const WORKSHEET_TYPE = [
  'CUSTOM',
  'RECOMMEND',
  'PRACTICE_TEST',
  'ARITHMETIC',
  'ENTRANCE',
  'WEEKLY',
  'CHAPTER',
  'SCHOOL_PREPARE_TEXTBOOK',
  'PRACTICE_TEST_PAIR',
  'EXAM',
  'SAT_LECTURE',
  'INTERNAL_MANAGEMENT',
  'CHALLENGE',
  'SELF_LEARNING',
  'SUPPLEMENTARY_WRONG',
  'SUPPLEMENTARY_LEVEL_UP',
  'SUPPLEMENTARY_WRONG_CHALLENGE',
  'CONCEPTUAL',
  'SCHOOL_TEST',
  'SAT_COMPLETION',
  'UNIVERSITY_PRACTICE_TEST',
  'UNIVERSITY_PRACTICE_TEST_PAIR',
  'SCHOOL_PREPARE_ESSAY',
  'DAILY',
  'CREVERSE_ENTRANCE',
] as const

/***************** 학습지 태그 *****************/
export const WORKSHEET_TAG = [
  'BASIC',
  'PRACTICE',
  'HOMEWORK',
  'DAILY_TEST',
  'WEEKLY_TEST',
  'CHAPTER_TEST',
  'ENTRANCE_TEST',
  'SCHOOL_PREPARE',
  'PRACTICE_TEST',
  'CSA_TEST',
  'GENERAL_TEST',
  'WEAK_CHAPTER',
  'WEAK_DATE',
  'WEAK_WORKSHEET',
  'WEAK_WORKBOOK',
  'ETC',
  'PRACTICE_TEST_PAIR',
  'ARITHMETIC',
  'CONCEPT_CHIP',
  'CONCEPT_CHIP_WEAK_PROBLEM',
  'CUSTOM_PAPER',
  'SCHOOL_TEST_PAPER',
  'REVIEW',
  'SCHOOL_PREPARE_ESSAY',
  'WEAK_CONCEPT_CHIP',
  'WEAK_CONCEPT_CHIP_BY_GROUP',
  'DAILY_WORKSHEET',
  'MY_DB_ORIGINAL',
  'PRACTICE_TEST_SIMILAR',
  'CREVERSE_ENTRANCE',
  'WEAK_ARITHMETIC',
] as const

/***************** 학습지 생성 필터 종류 *****************/
export const WORKSHEET_FILTER_TYPE = [
  'CONCEPT',
  'PUBLIC_WORKBOOK',
  'PRACTICE_TEST',
  'WEAK_CONCEPT',
  'WEAK_PERIOD',
  'WEAK_WORKSHEET',
  'SCHOOL_TEST',
  'CUSTOM_PAPER',
  'SCHOOL_TEST_PAPER',
  'ONE_CLICK',
  'MERGED_WORKSHEET',
  'DAILY_WORKSHEET',
  'MY_DB_ORIGINAL',
  'MY_DB_SIMILAR',
  'DAILY_WRONG_LEARNING',
] as const

/***************** 자기주도학습 종류 *****************/
export const SELF_LEARNING_TYPE = ['WRONG', 'LEVEL_UP'] as const

/***************** 학습지 병합 시 문제 정렬 *****************/
export const WORKSHEET_MERGE_PROBLEM_SORT_TYPE = ['BY_CONCEPT', 'BY_WORKSHEET'] as const

/***************** 학생-학습물 학습 상태 *****************/
// 학생에게 배정된 학습물(학습지·시험·교재 페이지·숙제)의 진행 상태. 서버는 학습물마다 enum 을 따로 두고
// (StudentWorksheetStatus·StudentHomeworkStatus 는 앞의 셋, StudentExamStatus 는 EXPIRED 까지) 값 이름이
// 같아 하나의 어휘로 둔다. 서버의 ContentStatus(게시 상태)와는 다른 것이다.
export const LEARNING_STATUS = ['INCOMPLETE', 'PROGRESS', 'COMPLETE', 'EXPIRED'] as const

/***************** 연산 학습지 묶음 내 역할 *****************/
// 한 소단원의 연산 학습지 묶음에서 대표(MASTER)와 나머지(SLAVE)
export const ARITHMETIC_WORKSHEET_TYPE = ['MASTER', 'SLAVE'] as const

/***************** 예약 출제 상태 *****************/
// 서버 내부의 실행 상태 다섯을 화면 탭 셋으로 접은 값
export const WORKSHEET_ASSIGN_SCHEDULE_STATUS = ['WAITING', 'DONE', 'FAILED'] as const

/***************** 예약 출제 실패 범위 *****************/
export const WORKSHEET_ASSIGN_SCHEDULE_FAILURE_SCOPE = ['PARTIAL', 'ALL'] as const

/***************** 학습지 디자인 템플릿 배율 *****************/
export const WORKSHEET_DESIGN_TEMPLATE_SCALE = ['DEFAULT', 'MAGNIFY_110'] as const

/***************** 학습지 HWP 구성물 *****************/
export const WORKSHEET_HWP_CONTENT_TYPE = [
  'ELEMENT',
  'QUICK_ANSWER',
  'ANSWER_SOLUTION',
  'OMR',
] as const

/***************** 학습지 HWP 파일 생성 상태 *****************/
export const WORKSHEET_HWP_FILE_STATUS = ['REQUESTED', 'COMPLETED', 'FAILED'] as const

/***************** 학습지 HWP 다운로드 한도 단위 *****************/
// 무료체험 학원은 학습지 건수, 정식·B2G 학원은 문항 수로 센다
export const WORKSHEET_HWP_QUOTA_LIMIT_TYPE = ['WORKSHEET_COUNT', 'PROBLEM_COUNT'] as const

export const WorksheetTypeSchema = z.enum(WORKSHEET_TYPE)
export type WorksheetType = z.infer<typeof WorksheetTypeSchema>

export const WorksheetTagSchema = z.enum(WORKSHEET_TAG)
export type WorksheetTag = z.infer<typeof WorksheetTagSchema>

export const WorksheetFilterTypeSchema = z.enum(WORKSHEET_FILTER_TYPE)
export type WorksheetFilterType = z.infer<typeof WorksheetFilterTypeSchema>
/** 오답학습지(단원별·기간별·학습지별) 필터 종류 */
export type WrongWorksheetFilterType = Extract<
  WorksheetFilterType,
  'WEAK_CONCEPT' | 'WEAK_PERIOD' | 'WEAK_WORKSHEET'
>

export const SelfLearningTypeSchema = z.enum(SELF_LEARNING_TYPE)
export type SelfLearningType = z.infer<typeof SelfLearningTypeSchema>

export const WorksheetMergeProblemSortTypeSchema = z.enum(WORKSHEET_MERGE_PROBLEM_SORT_TYPE)
export type WorksheetMergeProblemSortType = z.infer<typeof WorksheetMergeProblemSortTypeSchema>

export const LearningStatusSchema = z.enum(LEARNING_STATUS)
export type LearningStatus = z.infer<typeof LearningStatusSchema>

export const ArithmeticWorksheetTypeSchema = z.enum(ARITHMETIC_WORKSHEET_TYPE)
export type ArithmeticWorksheetType = z.infer<typeof ArithmeticWorksheetTypeSchema>

export const WorksheetAssignScheduleStatusSchema = z.enum(WORKSHEET_ASSIGN_SCHEDULE_STATUS)
export type WorksheetAssignScheduleStatus = z.infer<typeof WorksheetAssignScheduleStatusSchema>

export const WorksheetAssignScheduleFailureScopeSchema = z.enum(
  WORKSHEET_ASSIGN_SCHEDULE_FAILURE_SCOPE,
)
export type WorksheetAssignScheduleFailureScope = z.infer<
  typeof WorksheetAssignScheduleFailureScopeSchema
>

export const WorksheetDesignTemplateScaleSchema = z.enum(WORKSHEET_DESIGN_TEMPLATE_SCALE)
export type WorksheetDesignTemplateScale = z.infer<typeof WorksheetDesignTemplateScaleSchema>

export const WorksheetHwpContentTypeSchema = z.enum(WORKSHEET_HWP_CONTENT_TYPE)
export type WorksheetHwpContentType = z.infer<typeof WorksheetHwpContentTypeSchema>

export const WorksheetHwpFileStatusSchema = z.enum(WORKSHEET_HWP_FILE_STATUS)
export type WorksheetHwpFileStatus = z.infer<typeof WorksheetHwpFileStatusSchema>

export const WorksheetHwpQuotaLimitTypeSchema = z.enum(WORKSHEET_HWP_QUOTA_LIMIT_TYPE)
export type WorksheetHwpQuotaLimitType = z.infer<typeof WorksheetHwpQuotaLimitTypeSchema>

// 학습지가 파생된 원본 교재. 학습지 응답이 싣는 넷만 받는다.
export const OriginWorkbookSchema = z.object({
  id: z.number(),
  type: WorkbookTypeSchema,
  fulltitle: z.string(),
  securingType: WorkbookSecuringTypeSchema.optional(),
})

export const WorksheetSchema = z.object({
  id: z.number(),
  type: WorksheetTypeSchema,
  filterType: WorksheetFilterTypeSchema.nullish(),
  revision: RevisionSchema,
  school: SchoolTypeSchema,
  grade: AllGradeSubjectSchema,
  title: z.string(),
  titleTag: z.string(),
  chapter: z.string(),
  tag: WorksheetTagSchema,
  problemCount: z.number(),
  level: ProblemLevelSchema,
  autoScorable: z.boolean(),
  accessModifierToStudent: AccessModifierToStudentSchema,
  originWorkbook: OriginWorkbookSchema.nullish(),
  isArithmetic: z.boolean(),
})
export type Worksheet = z.infer<typeof WorksheetSchema>

// 자기주도학습(심화·오답)으로 만들어진 학습지. 서버는 WorksheetResponse 를 상속한 SelfLearningWorksheetResponse 로
// 내므로 학습지 전체 위에 종류가 얹힌다. 2026-09-29 백엔드 develop
// studentWorksheet/controller/response/SelfLearningWorksheetResponse.kt 로 확인.
export const SelfLearningWorksheetSchema = WorksheetSchema.extend({
  selfLearningType: SelfLearningTypeSchema,
})
export type SelfLearningWorksheet = z.infer<typeof SelfLearningWorksheetSchema>

// 학습지에 실린 문제 한 칸. 문제은행의 Problem 과는 다른 것으로, 학습지 안의 위치와 문제 참조만 갖는다.
export const WorksheetProblemSchema = z.object({
  id: z.number(),
  worksheetId: z.number(),
  problemId: z.number(),
  orderingNumber: z.number(),
  tagTop: z.string().optional(),
  baseWorkbookProblemId: z.number().optional(),
})
export type WorksheetProblem = z.infer<typeof WorksheetProblemSchema>

/***************** 학습지 배정 *****************/

/** 학습지 하나에 배정된 학생 한 명. 퇴원·삭제 학생도 배정 기록이 남아 학생 상태를 함께 싣는다 */
export const WorksheetAssigneeSchema = z.object({
  // 학생-학습지(StudentWorksheet) id
  studentWorksheetId: z.number(),
  studentId: StudentIdSchema,
  studentName: z.string(),
  studentStatus: StudentStatusSchema,
  status: LearningStatusSchema,
})
export type WorksheetAssignee = z.infer<typeof WorksheetAssigneeSchema>

/***************** 선생님 학습지 목록 *****************/
// 수업 자료의 학습지 탭(내 학습지·내신대비·모의고사·단원TEST 등)은 탭마다 응답 클래스가 따로이고, 학생 앱이 받는
// 학습지(WorksheetSchema)와 달리 학교급을 schoolType 으로 내며 학습지 종류(type)·연산 여부(isArithmetic)가 없다.
// 모든 탭에 있는 것을 공통부로 두고 탭마다 더 오는 것을 붙인다. 앱이 읽지 않는 rate(정답률)·year·month·
// 모의고사 과목(type)·universityType·satWorksheetType·satCompletionType 은 받지 않는다.

const _localDatetime = z.iso.datetime({ local: true })

export const WorksheetListItemSchema = z.object({
  id: WorksheetSchema.shape.id,
  revision: RevisionSchema,
  schoolType: SchoolTypeSchema,
  grade: AllGradeSubjectSchema,
  title: z.string(),
  titleTag: z.string(),
  chapter: z.string(),
  tag: WorksheetTagSchema,
  problemCount: z.number(),
  autoScorable: z.boolean(),
  assignList: z.array(WorksheetAssigneeSchema),
})
export type WorksheetListItem = z.infer<typeof WorksheetListItemSchema>

/** 내신대비·내신대비 서술형·입학TEST·단원TEST·총괄TEST·주간TEST·KMM 탭의 한 줄 */
export const LeveledWorksheetListItemSchema = WorksheetListItemSchema.extend({
  level: ProblemLevelSchema,
  createDatetime: _localDatetime,
})
export type LeveledWorksheetListItem = z.infer<typeof LeveledWorksheetListItemSchema>

/** 내신대비 교과서 탭의 한 줄. 생성일이 없다 */
export const TextbookWorksheetListItemSchema = WorksheetListItemSchema.extend({
  level: ProblemLevelSchema,
  publisher: z.string(),
})
export type TextbookWorksheetListItem = z.infer<typeof TextbookWorksheetListItemSchema>

/** 수능특강·수능완성 탭의 한 줄. 생성일이 없다 */
export const SatWorksheetListItemSchema = WorksheetListItemSchema.extend({
  level: ProblemLevelSchema,
})
export type SatWorksheetListItem = z.infer<typeof SatWorksheetListItemSchema>

/** 수능·모의고사와 경찰대·사관학교 탭의 한 줄. 난이도가 없고 출제 기관(writer)이 온다 */
export const PracticeTestWorksheetListItemSchema = WorksheetListItemSchema.extend({
  createDatetime: _localDatetime,
  writer: z.string(),
})
export type PracticeTestWorksheetListItem = z.infer<typeof PracticeTestWorksheetListItemSchema>

// 예약 출제 한 건의 id
export const WorksheetAssignScheduleIdSchema = z.string()

/** 학원 범위의 대기 중 예약 출제. 둘 이상이면 가장 먼저 실행될 한 건이다 */
export const WorksheetPendingScheduleSchema = z.object({
  batchId: WorksheetAssignScheduleIdSchema,
  scheduledAt: _localDatetime,
  pendingCount: z.number(),
})
export type WorksheetPendingSchedule = z.infer<typeof WorksheetPendingScheduleSchema>

// 내 학습지(학원·학원그룹·휴지통) 탭의 한 줄. 공유 범위(shareScope)·개념 정렬(conceptSortType)·원본 교재
// (originWorkbook)는 앱이 읽지 않아 받지 않는다. baseWorkbookType 은 원본 교재의 종류다.
export const MyWorksheetListItemSchema = LeveledWorksheetListItemSchema.extend({
  writer: z.string(),
  filterType: WorksheetFilterTypeSchema,
  accessModifierToStudent: AccessModifierToStudentSchema,
  baseWorkbookType: WorkbookTypeSchema.nullable(),
  // 원본 학습지(MY_DB_ORIGINAL)만 서버가 채운다. 재다운로드면 HWP 수량을 다시 쓰지 않는다
  hwpDownloaded: z.boolean().nullable(),
  // 예약 출제를 쓸 수 없는 계정은 항상 null 이다
  pendingSchedule: WorksheetPendingScheduleSchema.nullable(),
})
export type MyWorksheetListItem = z.infer<typeof MyWorksheetListItemSchema>

/** 연산 학습지 한 장. 초등 연산 목록과 연산 묶음이 이 모양으로 온다 */
export const ArithmeticWorksheetSchema = z.object({
  id: WorksheetSchema.shape.id,
  title: z.string(),
  type: ArithmeticWorksheetTypeSchema,
  thumbnail: z.string(),
  problemCount: z.number(),
  orderingNumber: z.number(),
  sectionOrderingNumber: z.number().nullable(),
})
export type ArithmeticWorksheet = z.infer<typeof ArithmeticWorksheetSchema>

/** 중·고등 연산 묶음의 학습지 한 장. 학습지 메타와 배정 학생이 더 온다 */
export const AssignableArithmeticWorksheetSchema = ArithmeticWorksheetSchema.extend(
  LeveledWorksheetListItemSchema.omit({ id: true, title: true, problemCount: true }).shape,
)
export type AssignableArithmeticWorksheet = z.infer<typeof AssignableArithmeticWorksheetSchema>

/** 교육과정 노드 하나에 묶인 연산 학습지. 초등은 연산 학습지만, 중·고등은 배정 정보까지 온다 */
export const ArithmeticWorksheetGroupSchema = z.object({
  curriculumKey: z.string(),
  curriculumType: CurriculumTypeSchema,
  curriculumName: z.string(),
  // 배정 정보가 있는 쪽을 먼저 맞춘다. 반대로 두면 z.object 가 남는 키를 버려 배정 정보를 잃는다.
  worksheet: z.array(z.union([AssignableArithmeticWorksheetSchema, ArithmeticWorksheetSchema])),
})
export type ArithmeticWorksheetGroup = z.infer<typeof ArithmeticWorksheetGroupSchema>

/** 학교별 기출과 비슷한 학습지 한 장. 학교는 이름만 읽어 나머지(코드·지역·상태)는 받지 않는다 */
export const SchoolTestSimilarWorksheetSchema = z.object({
  id: WorksheetSchema.shape.id,
  title: z.string(),
  chapter: z.string(),
  year: z.number(),
  grade: AllGradeSubjectSchema,
  semester: SemesterSchema,
  schoolTestType: SchoolTestTypeSchema,
  school: z.object({ name: z.string() }),
  region: RegionSchema,
  assignList: z.array(WorksheetAssigneeSchema),
})
export type SchoolTestSimilarWorksheet = z.infer<typeof SchoolTestSimilarWorksheetSchema>

/** 학교별 기출과 비슷한 학습지의 범위별 수 */
export const SchoolTestSimilarWorksheetCountSchema = z.object({
  sameSchool: z.number(),
  sameRegion: z.number(),
  neighborhoodRegion: z.number(),
  specialEducationRegion: z.number(),
})
export type SchoolTestSimilarWorksheetCount = z.infer<typeof SchoolTestSimilarWorksheetCountSchema>

/***************** 예약 출제 *****************/
// 실행은 batch-server 가 30분 슬롯마다 한다. 예약 한 건(batchId)은 학습지 하나와 학생 여럿을 묶는다.

/** 대기 중인 예약 대상 하나 — 학습지 하나와 학생 한 명 */
export const WorksheetAssignScheduleTargetSchema = z.object({
  batchId: WorksheetAssignScheduleIdSchema,
  worksheetId: WorksheetSchema.shape.id,
  studentId: StudentIdSchema,
  scheduledAt: _localDatetime,
})
export type WorksheetAssignScheduleTarget = z.infer<typeof WorksheetAssignScheduleTargetSchema>

export const WorksheetAssignScheduleStudentSchema = z.object({
  studentId: StudentIdSchema,
  // 삭제·퇴원 학생은 이름이 null 이고 행은 남는다
  studentName: z.string().nullable(),
  status: WorksheetAssignScheduleStatusSchema,
  // batch-server 가 쓰는 사유 코드다. 새 코드가 늘 수 있어 값 집합을 닫지 않는다
  failureReason: z.string().nullable(),
})
export type WorksheetAssignScheduleStudent = z.infer<typeof WorksheetAssignScheduleStudentSchema>

/** 예약 목록의 카드 하나 — 예약 한 건과 대상 학생 전원 */
export const WorksheetAssignScheduleSchema = z.object({
  batchId: WorksheetAssignScheduleIdSchema,
  teacherId: TeacherIdSchema,
  teacherName: z.string().nullable(),
  worksheetId: WorksheetSchema.shape.id,
  // 삭제된 학습지도 제목이 온다. 학습지를 찾지 못하면 null 이다
  worksheetTitle: z.string().nullable(),
  scheduledAt: _localDatetime,
  // 선생님이 반 탭으로 대상을 고른 경우에만 있다
  lessonClassId: LessonClassIdSchema.nullable(),
  status: WorksheetAssignScheduleStatusSchema,
  // status 가 FAILED 일 때만 있다
  failureScope: WorksheetAssignScheduleFailureScopeSchema.nullable(),
  targetCount: z.number(),
  // 탭과 무관하게 항상 전원이다
  students: z.array(WorksheetAssignScheduleStudentSchema),
})
export type WorksheetAssignSchedule = z.infer<typeof WorksheetAssignScheduleSchema>

export const WorksheetAssignScheduleCountSchema = z.object({
  waiting: z.number(),
  failed: z.number(),
  done: z.number(),
})
export type WorksheetAssignScheduleCount = z.infer<typeof WorksheetAssignScheduleCountSchema>

/** 예약 등록 결과 */
export const WorksheetAssignScheduleCreationSchema = z.object({
  batchId: WorksheetAssignScheduleIdSchema,
  targetCount: z.number(),
})
export type WorksheetAssignScheduleCreation = z.infer<typeof WorksheetAssignScheduleCreationSchema>

/***************** 학습지 디자인 *****************/

/** 학습지 지면 디자인 템플릿. id 가 학습지·디자인 옵션의 layoutType 이 된다 */
export const WorksheetDesignTemplateSchema = z.object({
  id: z.number(),
  // 목록 정렬 순서
  index: z.number(),
  name: z.string(),
  // 미리보기 이미지
  url: z.string(),
  type: WorksheetDesignTemplateScaleSchema,
  // 서버는 템플릿 설정을 Map 그대로 낸다. 앱은 QR 지원 여부만 읽고, 템플릿마다 키가 없을 수 있다.
  metadata: z.object({ qrAvailable: z.boolean().optional() }),
})
export type WorksheetDesignTemplate = z.infer<typeof WorksheetDesignTemplateSchema>

/** 선생님이 저장해 둔 학습지 디자인 옵션의 목록 줄 */
export const WorksheetDesignSchema = z.object({
  id: z.number(),
  name: z.string(),
})
export type WorksheetDesign = z.infer<typeof WorksheetDesignSchema>

/** 학습지 인쇄 모양. 디자인 옵션으로 저장해 두거나 학습지 PDF 에 실린다 */
export const WorksheetPrintOptionSchema = z.object({
  layoutType: WorksheetDesignTemplateSchema.shape.id,
  layoutColor: LayoutColorSchema,
  partitionType: PartitionTypeSchema,
  pdfDateType: PdfDateTypeSchema,
  wrongAnswerNoteFlag: z.boolean(),
  conceptNameFlag: z.boolean(),
  problemTrendFlag: z.boolean(),
  answerRateFlag: z.boolean(),
  qrFlag: z.boolean(),
  relationWorkbookFlag: z.boolean(),
  includeProblemFlag: z.boolean(),
})
export type WorksheetPrintOption = z.infer<typeof WorksheetPrintOptionSchema>

/** 저장된 학습지 디자인 옵션 하나 */
export const WorksheetDesignDetailSchema = WorksheetDesignSchema.extend(
  WorksheetPrintOptionSchema.shape,
)
export type WorksheetDesignDetail = z.infer<typeof WorksheetDesignDetailSchema>

/***************** 학습지 파일 *****************/

/** 학습지 PDF 내려받기·미리보기 주소 */
export const WorksheetFileSchema = z.object({
  presignedUrl: z.string(),
  fileName: z.string(),
})
export type WorksheetFile = z.infer<typeof WorksheetFileSchema>

/** HWP 생성 요청 접수. 이 id 로 파일이 준비됐는지 다시 묻는다 */
export const WorksheetHwpRequestSchema = z.object({
  requestId: z.string(),
})
export type WorksheetHwpRequest = z.infer<typeof WorksheetHwpRequestSchema>

/** 학습지 하나의 HWP 파일. 생성이 끝나기 전이나 실패하면 주소가 없다 */
export const WorksheetHwpFileSchema = z.object({
  worksheetId: WorksheetSchema.shape.id,
  presignedUrl: z.string().nullable(),
  fileName: z.string(),
  status: WorksheetHwpFileStatusSchema,
})
export type WorksheetHwpFile = z.infer<typeof WorksheetHwpFileSchema>

/** 무료체험 학원의 원본 학습지 제작 수량. 체험 학원이 아니면 limited 가 false 이고 나머지는 0 이다 */
export const OriginalWorksheetQuotaSchema = z.object({
  limited: z.boolean(),
  used: z.number(),
  limit: z.number(),
  remaining: z.number(),
})
export type OriginalWorksheetQuota = z.infer<typeof OriginalWorksheetQuotaSchema>

/** 원본 학습지 HWP 다운로드 한도. 비활성 학원이면 limited 가 false, limitType 이 null 이고 나머지는 0 이다 */
export const WorksheetHwpQuotaSchema = z.object({
  limited: z.boolean(),
  limitType: WorksheetHwpQuotaLimitTypeSchema.nullable(),
  used: z.number(),
  limit: z.number(),
  remaining: z.number(),
})
export type WorksheetHwpQuota = z.infer<typeof WorksheetHwpQuotaSchema>

/** 원본 학습지 HWP 다운로드 문항당 단가(원, VAT 별도). 학원과 무관한 전역 정책이다 */
export const WorksheetHwpUnitPriceSchema = z.object({
  originalUnitPrice: z.number(),
  appliedUnitPrice: z.number(),
  discountLabel: z.string().nullable(),
  periodStartDate: z.iso.date(),
  /** 무기한이면 9999-12-31 */
  periodEndDate: z.iso.date(),
})
export type WorksheetHwpUnitPrice = z.infer<typeof WorksheetHwpUnitPriceSchema>

/** 원본 학습지 HWP 구매 한 건. 학습지별 최초 다운로드만 실린다 */
export const WorksheetHwpPurchaseSchema = z.object({
  purchasedAt: z.iso.datetime(),
  teacherId: TeacherIdSchema,
  teacherName: TeacherSchema.shape.name,
  worksheetId: WorksheetSchema.shape.id,
  worksheetTitle: WorksheetSchema.shape.title,
  problemCount: WorksheetSchema.shape.problemCount,
  /** 청구 금액(원). 이벤트 등으로 무과금이면 0 */
  billedAmount: z.number(),
})
export type WorksheetHwpPurchase = z.infer<typeof WorksheetHwpPurchaseSchema>

/** 학원 결제 주기(사용기간) 하나의 원본 학습지 HWP 구매 내역 */
export const WorksheetHwpPurchaseHistorySchema = z.object({
  periodStartDate: z.iso.date(),
  periodEndDate: z.iso.date(),
  paymentDate: z.iso.date(),
  /** 재다운로드까지 센 다운로드 횟수라 items 길이와 다를 수 있다 */
  downloadCount: z.number(),
  totalProblemCount: z.number(),
  totalBilledAmount: z.number(),
  items: z.array(WorksheetHwpPurchaseSchema),
})
export type WorksheetHwpPurchaseHistory = z.infer<typeof WorksheetHwpPurchaseHistorySchema>

/***************** 학습지 과다 생성 캡차 *****************/

// 교사당 활성 챌린지는 하나라 재발급해도 같은 challengeId 가 온다. type(지금은 MATH_PROBLEM 하나)·imageUrl 은
// 받지 않는다. 이미지는 JWT 헤더 인증이라 <img src> 로 열 수 없어 앱이 이미지 엔드포인트를 blob 으로 받는다.
export const WorksheetCaptchaChallengeSchema = z.object({
  challengeId: z.string(),
  attemptsLeft: z.number(),
})
export type WorksheetCaptchaChallenge = z.infer<typeof WorksheetCaptchaChallengeSchema>

export const WorksheetCaptchaVerificationSchema = z.object({
  verified: z.boolean(),
})
export type WorksheetCaptchaVerification = z.infer<typeof WorksheetCaptchaVerificationSchema>

/***************** 다른 기관 학습지 (검색 서버) *****************/

// 검색 서버(search-api)가 OpenSearch 색인에서 읽어 내는 항목. 메인 API 의 학습지와 필드명이 일부 다르다
// (titlePrefix·createdAt·autoScored). 서버 선언(WorksheetSearchItem)이 id 를 빼고 모두 nullable 이라 그대로 받는다.
// 2026-10-13 mathFLAT/freewheelin-search-server main 소스로 확인. revision·grade·filterType·tag 는 색인 라벨을
// 그대로 옮긴 문자열이라 닫힌 목록으로 좁히지 않는다.
export const SharedCustomWorksheetSchema = z.object({
  id: z.number(),
  revision: z.string().nullish(),
  schoolType: SchoolTypeSchema.nullish(),
  grade: z.string().nullish(),
  filterType: z.string().nullish(),
  title: z.string().nullish(),
  titlePrefix: z.string().nullish(),
  chapter: z.string().nullish(),
  tag: z.string().nullish(),
  level: ProblemLevelSchema.nullish(),
  autoScored: z.boolean().nullish(),
  writer: z.string().nullish(),
  problemCount: z.number().nullish(),
  createdAt: z.iso.datetime({ local: true }).nullish(),
})
export type SharedCustomWorksheet = z.infer<typeof SharedCustomWorksheetSchema>
